import { FC, ChangeEvent, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Divider, Menu, SideNavigation } from '@robocorp/components';
import { IconPlus, IconUsers, IconSearch } from '@robocorp/icons/iconic';
import { useCurrentOrFirstOrganization } from '~/queries';
import { withErrorBoundary } from '~/hocs';
import { CurrentSsoRestriction, CurrentUserRestriction, NavigationLink } from '~/components';
import { useActions } from '~/hooks';
import { OrganizationSearchResults } from './SearchResults';
import { OrganizationList } from './List';

const MenuContent: FC = () => {
  const { t } = useTranslation();

  const [searchTerm, setSearchTerm] = useState<string>('');
  const onSearchTermChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value),
    [],
  );

  const { toggleOrganizationDialog } = useActions(({ uiActions }) => ({
    toggleOrganizationDialog: uiActions.toggleOrganizationDialog,
  }));

  return (
    <>
      <Menu.Input
        iconLeft={IconSearch}
        placeholder={t('searchOrganizations')}
        value={searchTerm}
        onChange={onSearchTermChange}
        size="small"
        aria-label={t('searchOrganizations') as string}
        autoFocus
      />

      {searchTerm ? <OrganizationSearchResults search={searchTerm} /> : <OrganizationList />}

      <CurrentUserRestriction permission="canManageOrganizations">
        <Divider />

        <Menu.Item icon={IconPlus} onClick={toggleOrganizationDialog} role="menuitem">
          {t('newOrganization')}
        </Menu.Item>
      </CurrentUserRestriction>

      <CurrentSsoRestriction permission="isSsoAdmin">
        <Divider />

        <NavigationLink component={Menu.Link} icon={IconUsers} to="/settings/sso-management">
          {t('ssoManagement.title')}
        </NavigationLink>
      </CurrentSsoRestriction>
    </>
  );
};

const MenuBase: FC = () => {
  const { data: currentOrganization } = useCurrentOrFirstOrganization();

  // Hide organization menu if there aren't any organizations
  if (!currentOrganization) {
    return null;
  }

  const initials = currentOrganization?.name.charAt(0) || '';

  return (
    <Menu
      trigger={
        currentOrganization.details.picture ? (
          <SideNavigation.Header
            src={currentOrganization.details.picture}
            alt={currentOrganization.name}
            placeholder={initials}
            data-testid="application__sidebar-menu"
          >
            {currentOrganization.name}
          </SideNavigation.Header>
        ) : (
          <SideNavigation.Header placeholder={initials} data-testid="application__sidebar-menu">
            {currentOrganization.name}
          </SideNavigation.Header>
        )
      }
      initialFocus={1}
      width={254}
    >
      <MenuContent />
    </Menu>
  );
};

export const OrganizationMenu = withErrorBoundary(MenuBase);
